import api from '../../utils/axios';
import { toast } from '../../utils/toast';
import { useState, useCallback } from 'react';
import Swal from 'sweetalert2';

export interface InventarioItem {
  Id: number;
  IdArticulo: number;
  IdSucursal: number;
  stock: number;
  StockMinimo: number;
  articulo?: {
    Id: number;
    Nombre: string;
    Descripcion?: string;
  };
  sucursal?: {
    Id: number;
    Nombre: string;
  };
}

export interface InventarioDto {
  IdArticulo: number;
  IdSucursal: number;
  stock: number;
  StockMinimo: number;
}

export interface StockMovimientoDto {
  IdInventario: number;
  cantidad: number;
  tipo: 'entrada' | 'salida';
  motivo?: string;
}

export interface ImportResult {
  success: InventarioItem[];
  errors: { articulo: string; error: string }[];
 }

export const useInventariosApi = () => {
  const [isLoading, setIsLoading] = useState(false);
  const baseUrl = '/almacen/inventarios';

  const handleError = (error: any) => {
    const message = error.response?.data?.message || 'Error en la operación';
    Swal.fire('Error', message, 'error');
    throw error;
  };

  const getAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.post<InventarioItem[]>(`${baseUrl}/findAll`);
      return response.data;
    } catch (error) {
      console.error('Error fetching inventarios:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const findOne = useCallback(async (id: number) => {
    setIsLoading(true);
    try {
      const response = await api.post<InventarioItem>(`${baseUrl}/findOne/${id}`);
      return response.data;
    } catch (error) {
      handleError(error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const create = useCallback(async (data: InventarioDto) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/create`, data);
      toast.success('Inventario creado correctamente');
      return response.data;
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const update = useCallback(async (id: number, data: InventarioDto) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/update/${id}`, data);
      toast.success('Inventario actualizado correctamente');
      return response.data;
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const remove = useCallback(async (id: number) => {
    setIsLoading(true);
    try {
      await api.post(`${baseUrl}/remove/${id}`);
      toast.success('Inventario eliminado correctamente');
      return true;
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateStock = useCallback(async (id: number, newStock: number) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/updateStock/${id}`, { stock: newStock });
      return response.data;
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const registrarMovimiento = useCallback(async (movimiento: StockMovimientoDto) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/registrarMovimiento`, movimiento);
      toast.success('Movimiento registrado correctamente');
      return response.data;
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const checkStockDisponible = useCallback(async (idArticulo: number, idSucursal: number, cantidad: number) => {
    try {
      const response = await api.post(`${baseUrl}/checkStock`, {
        IdArticulo: idArticulo,
        IdSucursal: idSucursal,
        cantidad
      });
      return response.data.disponible;
    } catch (error) {
      handleError(error);
      return false;
    }
  }, []);

  const getStockBajo = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.post<InventarioItem[]>(`${baseUrl}/stockBajo`);
      return response.data;
    } catch (error) {
      console.error('Error fetching stock bajo:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getByArticuloAndSucursal = useCallback(async (idArticulo: number, idSucursal: number) => {
    setIsLoading(true);
    try {
      const response = await api.post<InventarioItem>(`${baseUrl}/findByArticuloAndSucursal`, {
        IdArticulo: idArticulo,
        IdSucursal: idSucursal
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching inventario:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const downloadTemplate = useCallback(async () => {
    try {
      const response = await api.get(`${baseUrl}/download-template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_inventarios.xlsx');
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading template:', error);
      throw error;
    }
  }, []);

  const importExcel = useCallback(async (formData: FormData) => {
    setIsLoading(true);
    try {
      const response = await api.post<ImportResult>(`${baseUrl}/import-excel`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isLoading,
    getAll,
    findOne,
    create,
    update,
    remove,
    updateStock,
    registrarMovimiento,
    checkStockDisponible,
    getStockBajo,
    getByArticuloAndSucursal,
    downloadTemplate,
    importExcel
  };
};